import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * Reklaim deltas vs the v4 shadcn registry (preserved on REK-1563 Stack 2 refresh):
 * - `bg-popover text-popover-foreground` + `border` + `shadow-md` instead of the v4 default
 *   `bg-primary text-primary-foreground`. Reklaim's `--primary` is brand yellow (#FFDF03);
 *   using it as the tooltip surface would clash with the brand UX (yellow == "action /
 *   ownership", not "informational hint"). Tooltips stay on the popover surface palette.
 * - `text-sm` instead of `text-xs` for legibility at our default font scale.
 *
 * Adopted from the v4 registry (REK-1563 Stack 2):
 * - `<TooltipPrimitive.Portal>` wrapper -- ensures the tooltip renders in the document body
 *   instead of inline, fixing z-index stacking with Dialog / Sheet overlays.
 * - `origin-[--radix-tooltip-content-transform-origin]` -- makes the scale-in animation
 *   originate from the tooltip's anchor edge instead of the content centre.
 */
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
